import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, FreeMode, Mousewheel } from 'swiper/modules';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  IconWeight,
} from '@phosphor-icons/react';
import IconButton from '_components/UI/IconButton';
import 'swiper/css';

// ─── Context interface ────────────────────────────────────────────────────────

interface CarouselState {
  isPrevVisible: boolean;
  isNextVisible: boolean;
  /** False when every slide already fits without scrolling. */
  isScrollable: boolean;
}

interface CarouselActions {
  slidePrev: () => void;
  slideNext: () => void;
}

interface CarouselMeta {
  swiper: SwiperType | null;
  setSwiper: (s: SwiperType) => void;
  updateNavVisibility: (s: SwiperType) => void;
}

export interface CarouselContextValue {
  state: CarouselState;
  actions: CarouselActions;
  meta: CarouselMeta;
}

const CarouselContext = createContext<CarouselContextValue | null>(null);

export function useCarousel(): CarouselContextValue {
  const ctx = useContext(CarouselContext);
  if (!ctx)
    throw new Error('useCarousel must be used within Carousel.Provider');
  return ctx;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function Provider({ children }: { children: React.ReactNode }) {
  const [swiper, setSwiper] = useState<SwiperType | null>(null);
  const [isPrevVisible, setIsPrevVisible] = useState(false);
  const [isNextVisible, setIsNextVisible] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const updateNavVisibility = useCallback((s: SwiperType) => {
    setIsPrevVisible(!s.isBeginning);
    setIsNextVisible(!s.isEnd);
    // Stuck at both edges at once means every slide already fits.
    setIsScrollable(!(s.isBeginning && s.isEnd));
  }, []);

  const slidePrev = useCallback(() => swiper?.slidePrev(), [swiper]);
  const slideNext = useCallback(() => swiper?.slideNext(), [swiper]);

  // Memoized so the context value identity is stable across Provider re-renders
  // — otherwise every consumer (NavPrev, NavNext, ProductStripInner) re-renders
  // on each nav-visibility state change.
  const value = useMemo<CarouselContextValue>(
    () => ({
      state: { isPrevVisible, isNextVisible, isScrollable },
      actions: { slidePrev, slideNext },
      meta: { swiper, setSwiper, updateNavVisibility },
    }),
    [
      isPrevVisible,
      isNextVisible,
      isScrollable,
      slidePrev,
      slideNext,
      swiper,
      updateNavVisibility,
    ]
  );

  return (
    <CarouselContext.Provider value={value}>
      {children}
    </CarouselContext.Provider>
  );
}

interface TrackProps {
  children: React.ReactNode;
  onSlideChange?: (swiper: SwiperType) => void;
  className?: string;
  autoHeight?: boolean;
  spaceBetween?: number;
  breakpoints?: React.ComponentProps<typeof Swiper>['breakpoints'];
}

function Track({
  children,
  onSlideChange,
  className,
  autoHeight,
  spaceBetween = 2,
  breakpoints,
}: TrackProps) {
  const {
    meta: { setSwiper, updateNavVisibility },
  } = useCarousel();

  return (
    <Swiper
      modules={[A11y, FreeMode, Mousewheel]}
      className={className}
      slidesPerView="auto"
      spaceBetween={spaceBetween}
      breakpoints={breakpoints}
      initialSlide={0}
      watchSlidesProgress
      threshold={2}
      mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
      freeMode={{ enabled: true }}
      autoHeight={autoHeight}
      onSwiper={(s) => {
        setSwiper(s);
        updateNavVisibility(s);
      }}
      onSlideChange={(s) => {
        onSlideChange?.(s);
        updateNavVisibility(s);
      }}
      onReachBeginning={updateNavVisibility}
      onReachEnd={updateNavVisibility}
      onInit={updateNavVisibility}
      onResize={updateNavVisibility}
    >
      {children}
    </Swiper>
  );
}

interface NavButtonProps {
  'aria-label': string;
  className?: string;
  iconSize?: number;
  iconWeight?: IconWeight;
}

function NavPrev({
  'aria-label': ariaLabel,
  className,
  iconSize = 24,
  iconWeight = 'regular',
}: NavButtonProps) {
  const {
    state: { isPrevVisible },
    actions: { slidePrev },
  } = useCarousel();

  return (
    <IconButton
      icon={ArrowLeftIcon}
      type={isPrevVisible ? 'primary' : 'outline'}
      size="md"
      iconSize={iconSize}
      iconWeight={iconWeight}
      disabled={!isPrevVisible}
      onClick={slidePrev}
      aria-label={ariaLabel}
      className={className}
    />
  );
}

function NavNext({
  'aria-label': ariaLabel,
  className,
  iconSize = 24,
  iconWeight = 'regular',
}: NavButtonProps) {
  const {
    state: { isNextVisible },
    actions: { slideNext },
  } = useCarousel();

  return (
    <IconButton
      icon={ArrowRightIcon}
      type={isNextVisible ? 'primary' : 'outline'}
      size="md"
      iconSize={iconSize}
      iconWeight={iconWeight}
      disabled={!isNextVisible}
      onClick={slideNext}
      aria-label={ariaLabel}
      className={className}
    />
  );
}

// ─── Compound component ───────────────────────────────────────────────────────

const Carousel = {
  Provider,
  Track,
  Slide: SwiperSlide,
  NavPrev,
  NavNext,
};

export default Carousel;
